import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { api, ApiError } from "@main/api/client";
import { theme, card, btnPrimary, btnSecondary, inputStyle } from "@main/theme";
import { BugReportButton } from "@main/components/BugReportButton";

// ── Jersey edition onboarding ─────────────────────────────────────────────
// Two short steps: which certification the organisation is working towards
// (the Jersey edition offers Cyber Essentials and Cyber Essentials Plus only)
// and the few profile facts the platform needs. Operating location is Jersey
// by edition, so it is never asked; the regulated sector is asked afterwards
// by the shell's sector prompt, which is what the OES engine reads.

type Standard = "cyber_essentials" | "cyber_essentials_plus";

const STANDARDS: Array<{ id: Standard; name: string; body: string }> = [
  {
    id: "cyber_essentials",
    name: "Cyber Essentials",
    body: "A verified self-assessment against the five technical controls. The certificate and its ongoing surveillance are included in your package.",
  },
  {
    id: "cyber_essentials_plus",
    name: "Cyber Essentials Plus",
    body: "The same controls, independently tested on your devices by an assessor. £1,000 a year on top of your package.",
  },
];

const SIZES = ["1–10", "11–50", "51–200", "201–1,000", "1,000+"];

const label: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 6 };

export function JsyOnboardingPage({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState<0 | 1>(0);
  const [standard, setStandard] = useState<Standard>("cyber_essentials");
  const [profile, setProfile] = useState({ sector: "", size: "", ceo: "", itManager: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveStandard = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put("/organizations/frameworks", { frameworkIds: [standard] });
      setStep(1);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save your certification choice.");
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch("/organizations/profile", {
        sector: profile.sector || null,
        size: profile.size || null,
        ceo: profile.ceo || null,
        itManager: profile.itManager || null,
      });
      await api.post("/organizations/onboarding/complete", {});
      onComplete();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not complete setup.");
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: theme.color.bg, fontFamily: theme.font, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 16px" }}>
      <BugReportButton floating />
      <div style={{ ...card, width: "100%", maxWidth: 560 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.accentText, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>
          Abacas Jersey · Step {step + 1} of 2
        </div>

        {step === 0 && (
          <>
            <h1 style={{ fontSize: 20, color: theme.color.heading, margin: "0 0 6px" }}>Which certification are you working towards?</h1>
            <p style={{ fontSize: 13, color: theme.color.muted, margin: "0 0 18px", lineHeight: 1.6 }}>
              You can move between the two later from My Package.
            </p>
            <div style={{ display: "grid", gap: 10, marginBottom: 20 }}>
              {STANDARDS.map((s) => {
                const active = standard === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStandard(s.id)}
                    aria-pressed={active}
                    style={{
                      textAlign: "left", cursor: "pointer", padding: 14, borderRadius: theme.radius.md,
                      border: `1.5px solid ${active ? theme.color.accent : theme.color.border}`,
                      background: active ? theme.color.accentBg : "transparent", color: theme.color.text,
                      display: "flex", gap: 12, alignItems: "flex-start", fontFamily: "inherit",
                    }}
                  >
                    <ShieldCheck size={18} color={active ? "var(--c-accent)" : "var(--c-subtle)"} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: theme.color.heading }}>{s.name}</span>
                      <span style={{ display: "block", fontSize: 12, color: theme.color.muted, marginTop: 3, lineHeight: 1.5 }}>{s.body}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" onClick={saveStandard} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? "Saving…" : "Continue"}
              </button>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <h1 style={{ fontSize: 20, color: theme.color.heading, margin: "0 0 6px" }}>About your organisation</h1>
            <p style={{ fontSize: 13, color: theme.color.muted, margin: "0 0 18px", lineHeight: 1.6 }}>
              Your operating location is Jersey. Everything here can be changed later under My Organisation.
            </p>
            <div style={{ display: "grid", gap: 14, marginBottom: 20 }}>
              <div>
                <label style={label} htmlFor="jsy-sector">What does your organisation do?</label>
                <input id="jsy-sector" style={inputStyle} value={profile.sector} placeholder="e.g. Accountancy practice"
                  onChange={(e) => setProfile({ ...profile, sector: e.target.value })} />
              </div>
              <div>
                <label style={label} htmlFor="jsy-size">Approximate headcount</label>
                <select id="jsy-size" style={inputStyle} value={profile.size} onChange={(e) => setProfile({ ...profile, size: e.target.value })}>
                  <option value="">Select…</option>
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={label} htmlFor="jsy-ceo">Who runs the organisation?</label>
                <input id="jsy-ceo" style={inputStyle} value={profile.ceo} placeholder="Name of your director or owner"
                  onChange={(e) => setProfile({ ...profile, ceo: e.target.value })} />
              </div>
              <div>
                <label style={label} htmlFor="jsy-it">Who looks after your IT?</label>
                <input id="jsy-it" style={inputStyle} value={profile.itManager} placeholder="A person, or your IT provider"
                  onChange={(e) => setProfile({ ...profile, itManager: e.target.value })} />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button type="button" onClick={() => setStep(0)} disabled={saving} style={btnSecondary}>Back</button>
              <button type="button" onClick={finish} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? "Finishing…" : "Finish setup"}
              </button>
            </div>
          </>
        )}

        {error && <div role="alert" style={{ marginTop: 14, fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
      </div>
    </div>
  );
}
